/* eslint-disable */
import React, { useEffect, useState } from "react";
import Select from "react-select";
import useUserContext from "../../hooks/useUserContext";
import Spinner from "../../components/spinner/Spinner";
import DefaultState from "../../components/default_state/DefaultState";
import getAllDocuments from "./getAllDocuments";
import { COHATCH_MARKETS, COHATCH_LOCATIONS, COHATCH_MEETING_ROOMS } from "../../constant";
import MEDocument from "../../components/cohatch_documents/MEDocument";
import CCAuthorization from "../../components/cohatch_documents/CCAuthorizationDoc";
import COhatchMembershipTermsDoc from "../../components/cohatch_documents/COhatchMembershipTermsDoc";
import NHBTerms from "../../components/cohatch_documents/NHBTerms.jsx";
import NHBCateringForm from "../../components/cohatch_documents/NHBCateringDoc.jsx";
import resendDocument from "./resendDocument.js";
import notify from "../../services/toast.js";

function customTheme(theme) {
    return {
        ...theme,
        borderRadius: 5,
        colors: {
            ...theme.colors,
            primary25: "#ecf8ef",
            primary: "#56c271",
        },
    };
}

function AllDocuments() {
    const { userState } = useUserContext();
    const [documents, setDocuments] = useState([]);
    const [filteredDocuments, setFilteredDocuments] = useState([]);
    const [isDocumentsLoading, setIsDocumentsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [pagination, setPagination] = useState({
        total: 0,
        page: 1,
        remaining: 0,
        limit: 11,
    });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [document, setSelectedDocument] = useState(null);

    // State for filters
    const [filters, setFilters] = useState({
        documentType: "",
        market: "",
        location: "",
        status: "",
    });

    // Handle filter change
    const handleFilterChange = (selectedOption, { name }) => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            [name]: selectedOption ? selectedOption.value : "", // Handle null value if cleared
        }));
    };

    // Handle opening modal with selected document data
    const handleViewClick = (selectedDoc) => {
        setSelectedDocument(selectedDoc);
        setIsModalOpen(true);
        console.log("Selected document:", selectedDoc);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedDocument(null);  // Ensure document state is reset
    };

    // Mapping for document types
    const DOCUMENT_TYPES_MAP = {
        MEAgreement: "Meeting & Event Agreement",
        CCAuthorization: "Credit Card Authorization",
        COhatchMembershipTerms: "COhatch Membership Agreement",
        NHBTermsForm: "North High Brewing Terms and Conditons",
        NHBCateringForm: "North High Brewing Catering"

    };

    const documentTypeOptions = [
        { value: "", label: "Show All" },
        ...Object.entries(DOCUMENT_TYPES_MAP).map(([key, value]) => ({
            value: key,
            label: value,
        })),
    ];

    const marketOptions = [
        { value: "", label: "Show All" },
        ...COHATCH_MARKETS?.market?.map((market) => ({
            value: market.value,
            label: market.label,
        })),
    ];

    const locationOptions = filters.market
        ? [
            { value: "", label: "Show All" },
            ...COHATCH_LOCATIONS.cities.flatMap((city) => {
                const market = city[filters.market];
                return market?.locations?.map((location) => ({
                    value: location.value,
                    label: location.label,
                })) || [];
            }),
        ]
        : [
            { value: "", label: "Show All" },
        ];

    const statusOptions = [
        { value: "", label: "Show All" },
        { value: "Sent", label: "Sent" },
        { value: "Signed", label: "Signed" },
    ];

    // Get Document Type Label
    const getDocumentTypeLabel = (documentType) => DOCUMENT_TYPES_MAP[documentType] || documentType;

    // Get Market Label
    const getMarketLabel = (marketValue) => {
        const market = COHATCH_MARKETS?.market?.find(item => item.value === marketValue);
        return market ? market.label : marketValue;
    };

    // Get Location Label
    const getLocationLabel = (marketValue, locationValue) => {
        // Loop through all cities in COHATCH_LOCATIONS
        for (const city of COHATCH_LOCATIONS.cities) {
            if (city[marketValue]) {  // Check if the city has this market
                const market = city[marketValue];
                const locations = market.locations || []; // Get locations for the market
                const location = locations.find(item => item.value === locationValue);

                if (location) {
                    return location.label;
                } else {
                    console.error(`Location not found for market: ${marketValue}, location: ${locationValue}`);
                    return locationValue; // Fallback to the original value
                }
            }
        }
        return marketValue; // Fallback if market is not found
    };

    // Get Meeting Room Label
    const getMeetingRoomLabel = (marketValue, locationValue, roomValue) => {
        const rooms = COHATCH_MEETING_ROOMS?.[marketValue]?.[locationValue] || []; // Ensure valid nesting
        const room = rooms.find(item => item.value === roomValue);
        return room ? room.label : roomValue;
    };

    useEffect(() => {
        fetchAllDocuments(userState.accessToken, filters);
    }, [userState.accessToken, filters, searchTerm]);

    const fetchAllDocuments = async (accessToken, filters) => {
        try {
            setIsDocumentsLoading(true);

            // Fetch all documents (both COhatch and NHB) from updated backend
            const response = await getAllDocuments(
                accessToken,
                userState._id,
                1,      // First page
                99999,  // Large limit to fetch all
                filters
            );

            if (response.status === "success") {
                // Combine both COhatch and NHB documents
                const allDocuments = response.data || [];

                // Optionally tag them (if backend doesn’t already)
                const taggedDocs = allDocuments.map(doc => ({
                    ...doc,
                    sourceType: doc?.documentType?.toLowerCase().includes("nhb") ? "NHB" : "COhatch",
                }));

                setDocuments(taggedDocs);

                // Apply client-side filters
                const filteredDocs = applyFilters(taggedDocs, filters, searchTerm);
                setFilteredDocuments(filteredDocs);


                // Update pagination info
                setPagination((prev) => ({
                    ...prev,
                    total: filteredDocs.length,
                }));
            } else {
                console.error("Failed to fetch documents:", response.message || "Unknown error");
            }
        } catch (error) {
            console.error("Error while fetching documents", error);
        } finally {
            setIsDocumentsLoading(false);
        }
    };

    const applyFilters = (documents, filters, searchTerm) => {
        return documents.filter((document) => {
            const { documentType, market, location, status } = filters;
            let matches = true;

            // Filter by Document Type
            if (documentType && document.documentType !== documentType) matches = false;

            // Filter by Market
            if (market && document.meetingLocation?.market !== market) matches = false;

            // Filter by Location
            if (location && document.meetingLocation?.location !== location) matches = false;

            // Filter by Status
            if (status && ((status === "Signed" && !document.signature) || (status === "Sent" && document.signature))) matches = false;

            // 🔍 Search filter (case-insensitive)
            if (searchTerm) {
                const lowerSearch = searchTerm.toLowerCase();
                const searchableFields = [
                    document.documentType,
                    document.adminEmail,
                    document.emailAddress,
                    document.signature ? "signed" : "sent",
                    document.meetingLocation?.market,
                    document.meetingLocation?.location,
                ].filter(Boolean);

                const matchesSearch = searchableFields.some((field) =>
                    field.toLowerCase().includes(lowerSearch)
                );

                if (!matchesSearch) matches = false;
            }

            return matches;
        });
    };

    const handleNextPage = () => {
        if (pagination.page * pagination.limit < pagination.total) {
            setPagination(prev => ({
                ...prev,
                page: prev.page + 1
            }));
        }
    };

    const handlePrevPage = () => {
        if (pagination.page > 1) {
            setPagination(prev => ({
                ...prev,
                page: prev.page - 1
            }));
        }
    };

    const getPagedDocuments = () => {
        const startIndex = (pagination.page - 1) * pagination.limit;
        const endIndex = pagination.page * pagination.limit;
        return filteredDocuments.slice(startIndex, endIndex);
    };

    const handleResendEmails = async (doc) => {
        if (!doc || !doc._id) {
            console.error("Invalid document passed to handleResendEmails", doc);
            notify("Invalid document. Please try again.", "error");
            return;
        }

        if (doc.signature) {
            notify("Document is already signed. No need to resend.", "info");
            return;
        }

        const payload = {
            documentId: doc._id,
            emailAddress: doc.emailAddress,
            documentType: doc.documentType,
        };

        try {
            console.log(`Resending ${doc.documentType}...`);
            await resendDocument(userState.accessToken, payload);
            notify("Document resent successfully.", "success");
        } catch (error) {
            console.error("Error resending document:", error);
            notify("Failed to resend the document.", "error");
        }
    };


    return (
        <>
            <div className="bg-gray-100 rounded-3xl shadow-xl p-6 sm:p-8 space-y-8">
                <h2 className="text-3xl sm:text-5xl lg:text-5xl font-semibold text-success-800 font-bebas mb-6 sm:mb-8 text-center tracking-wide leading-tight">
                    All Documents
                </h2>
                {/* Filter Section */}
                <div className="mb-6 p-4 bg-white rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold mb-4">Filter Documents</h3>
                    <div className="flex flex-col">
                        <label htmlFor="search" className="mb-2 text-sm font-medium text-gray-700">Search</label>
                        <input
                            type="text"
                            id="search"
                            name="search"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search by type, email, or status..."
                            className="border border-gray-300 rounded-md p-2 text-sm focus:ring-primary-400 focus:border-primary-400"
                        />
                    </div>

                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {/* Document Type Select */}
                        <div className="flex flex-col">
                            <label htmlFor="documentType" className="mb-2 text-sm font-medium text-gray-700">Document Type</label>
                            <Select
                                name="documentType"
                                value={documentTypeOptions.find(option => option.value === filters.documentType)}
                                onChange={(selectedOption) => handleFilterChange(selectedOption, { name: "documentType" })}
                                options={documentTypeOptions}
                                placeholder="Select Document Type"
                                theme={customTheme}
                            />
                        </div>

                        {/* Market Select */}
                        <div className="flex flex-col">
                            <label htmlFor="market" className="mb-2 text-sm font-medium text-gray-700">Market</label>
                            <Select
                                name="market"
                                value={marketOptions.find(option => option.value === filters.market)}
                                onChange={(selectedOption) => handleFilterChange(selectedOption, { name: "market" })}
                                options={marketOptions}
                                placeholder="Select Market"
                                theme={customTheme}
                            />
                        </div>

                        {/* Location Select */}
                        <div className="flex flex-col">
                            <label htmlFor="location" className="mb-2 text-sm font-medium text-gray-700">Location</label>
                            <Select
                                name="location"
                                value={locationOptions.find(option => option.value === filters.location)}
                                onChange={(selectedOption) => handleFilterChange(selectedOption, { name: "location" })}
                                options={locationOptions}
                                placeholder="Select Location"
                                theme={customTheme}
                            />
                        </div>

                        {/* Status Select */}
                        <div className="flex flex-col">
                            <label htmlFor="status" className="mb-2 text-sm font-medium text-gray-700">Status</label>
                            <Select
                                name="status"
                                value={statusOptions.find(option => option.value === filters.status)}
                                onChange={(selectedOption) => handleFilterChange(selectedOption, { name: "status" })}
                                options={statusOptions}
                                placeholder="Select Status"
                                theme={customTheme}
                            />
                        </div>
                    </div>
                </div>

                {isDocumentsLoading ? (
                    <div className="flex justify-center items-center">
                        <Spinner displayText="Loading documents..." />
                    </div>
                ) : filteredDocuments.length > 0 ? (
                    <div className="overflow-x-auto bg-white shadow sm:rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Document Type
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Market & Location
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Sent By
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Received By
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Action
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Resend Email
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {getPagedDocuments().map((document) => (
                                    <tr key={document._id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {getDocumentTypeLabel(document.documentType)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {document.meetingLocation && (
                                                <div className="text-sm">
                                                    {document.documentType === "COhatchMembershipTerms" ? (
                                                        <>
                                                            <div>
                                                                <strong className="text-primary-400">Market:</strong> {getMarketLabel(document.meetingLocation.market)}
                                                            </div>
                                                            <div>
                                                                <strong className="text-success-800">Location:</strong> {getLocationLabel(document.meetingLocation.market, document.meetingLocation.location)}
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div>
                                                                <strong className="text-primary-400">Market:</strong> {getMarketLabel(document.meetingLocation.market)}
                                                            </div>
                                                            <div>
                                                                <strong className="text-success-800">Location:</strong> {getLocationLabel(document.meetingLocation.market, document.meetingLocation.location)}
                                                            </div>
                                                            {/* <div>
                                                                    <strong className="text-success-50">Meeting Room:</strong> {getMeetingRoomLabel(document.meetingLocation.market, document.meetingLocation.location, document.meetingLocation.room)}
                                                                </div> */}
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {document.adminEmail || "No Admin"}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {document.emailAddress || "No Email"}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {document?.signature ? "Signed" : "Sent"}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <button
                                                onClick={() => handleViewClick(document)}
                                                className="text-primary-400 hover:text-success-900"
                                            >
                                                View
                                            </button>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            {!document.signature && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleResendEmails(document)}
                                                    className="rounded-md bg-success-800 px-3 py-2 text-sm font-semibold text-white shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-success-50"
                                                >
                                                    Resend
                                                </button>

                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="flex justify-center">
                        <DefaultState
                            icon="file"
                            heading="No Documents Found"
                        />
                    </div>
                )}

                {pagination.total > pagination.limit && (
                    <div className="mt-8 flex justify-between">
                        {/* Hide Previous button if on the first page */}
                        {pagination.page > 1 && (
                            <button
                                onClick={handlePrevPage}
                                className="text-success-600 hover:text-success-900 font-medium"
                            >
                                Previous
                            </button>
                        )}

                        {/* Hide Next button if on the last page */}
                        {pagination.page * pagination.limit < pagination.total && (
                            <button
                                onClick={handleNextPage}
                                className="text-success-600 hover:text-success-900 font-medium"
                            >
                                Next
                            </button>
                        )}
                    </div>
                )}

                {isModalOpen && document && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 overflow-y-auto">
                        <div className="bg-white rounded-lg p-6 max-w-7xl w-full h-full max-h-screen overflow-y-scroll relative">
                            {/* Close button at top right */}
                            <button
                                onClick={handleCloseModal}
                                className="absolute top-4 right-4 bg-danger-500 text-white hover:bg-red-600 rounded-full p-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                            {/* Document content */}
                            {document.documentType === "MEAgreement" && <MEDocument document={document} />}
                            {document.documentType === "CCAuthorization" && <CCAuthorization document={document} />}
                            {document.documentType === "COhatchMembershipTerms" && <COhatchMembershipTermsDoc document={document} />}
                            {document.documentType === "NHBTermsForm" && <NHBTerms document={document} />}
                            {document.documentType === "NHBCateringForm" && <NHBCateringForm document={document} />}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}

export default AllDocuments;
